import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { MicButton } from "@/components/MicButton";
import { B2B } from "@/constants/testIds";

const inputCls = "w-full bg-[#FFFFFF] border border-[#E7DFD5] rounded-none px-4 py-3.5 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B] transition-colors duration-300";

const INTERESTS = [
  { key: "books", label: "Books · Kindle", tid: B2B.interestBooks },
  { key: "modules", label: "8 Modules", tid: B2B.interestModules },
  { key: "workshops", label: "Live Workshops", tid: B2B.interestWorkshops },
];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-2">{label}</span>
    {children}
  </label>
);

const CoachHandoffBanner = () => (
  <div data-testid="b2b-coach-handoff" className="flex items-start gap-3 p-4 border border-[#F59E0B]/30 bg-[#F59E0B]/5">
    <Sparkles size={16} className="text-[#B45309] shrink-0 mt-0.5" />
    <div>
      <div className="text-sm text-[#1C1917] font-medium mb-0.5">Proposal drafted from your chat.</div>
      <div className="text-xs text-[#78716C] leading-relaxed">We turned your conversation with the coach into a starting brief. Edit anything below before you send it.</div>
    </div>
  </div>
);

export const QuoteForm = ({ form, setForm, fromCoach, loading, onSubmit }) => {
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const toggleInterest = (v) => setForm((f) => ({
    ...f,
    interested_in: f.interested_in.includes(v) ? f.interested_in.filter((i) => i !== v) : [...f.interested_in, v],
  }));

  return (
    <motion.form
      data-testid={B2B.form}
      onSubmit={onSubmit}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
      className="col-span-12 md:col-span-7 md:col-start-6 space-y-6"
    >
      {fromCoach && <CoachHandoffBanner />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Company"><input required data-testid={B2B.company} value={form.company_name} onChange={set("company_name")} className={inputCls} placeholder="Acme Industries" /></Field>
        <Field label="Your Name"><input required data-testid={B2B.contact} value={form.contact_name} onChange={set("contact_name")} className={inputCls} placeholder="Jane Doe" /></Field>
        <Field label="Work Email"><input type="email" required data-testid={B2B.email} value={form.work_email} onChange={set("work_email")} className={inputCls} placeholder="jane@acme.com" /></Field>
        <Field label="Company Size">
          <select data-testid={B2B.size} value={form.company_size} onChange={set("company_size")} className={inputCls}>
            <option>1-24</option><option>25-100</option><option>100-500</option><option>500-2500</option><option>2500+</option>
          </select>
        </Field>
        <Field label="Industry"><input data-testid={B2B.industry} value={form.industry} onChange={set("industry")} className={inputCls} placeholder="Healthcare, Logistics, Retail…" /></Field>
        <Field label="Preferred Tier">
          <select data-testid={B2B.tier} value={form.tier} onChange={set("tier")} className={inputCls}>
            <option value="starter">Starter</option>
            <option value="team">Team</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </Field>
      </div>

      <Field label="Interested in">
        <div className="flex flex-wrap gap-3 pt-1">
          {INTERESTS.map((opt) => {
            const active = form.interested_in.includes(opt.key);
            return (
              <button
                type="button"
                key={opt.key}
                data-testid={opt.tid}
                onClick={() => toggleInterest(opt.key)}
                className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-[0.2em] border transition-colors duration-300 ${active ? "bg-[#F59E0B] text-black border-[#F59E0B]" : "border-[#E7DFD5] text-[#44403C] hover:border-[#F59E0B] hover:text-[#B45309]"}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="What are you trying to solve?">
        <textarea required data-testid={B2B.message} rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} placeholder="Burnout on our overnight shift, high attrition in Q3, we need something before performance reviews…" />
        <div className="mt-2 flex items-center gap-3">
          <MicButton
            testId="b2b-mic"
            size={40}
            context="A manager describing burnout and retention problems on their team."
            onText={(t) => setForm((f) => ({ ...f, message: f.message ? `${f.message} ${t}` : t }))}
          />
          <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#78716C]">Rather talk than type? Tap the mic</span>
        </div>
      </Field>

      <div className="flex items-center gap-4 pt-2">
        <button type="submit" disabled={loading} data-testid={B2B.submit} className="btn-ember text-sm">
          {loading ? "Sending…" : "Request a Quote"}
          <ArrowUpRight size={16} />
        </button>
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C]">One business day response</span>
      </div>
    </motion.form>
  );
};
